import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { DocStatusBadge, CATEGORY_LABELS } from "../admin/AuthorDocs";
import { MODULE_LABELS } from "../../components/shared";
import { toast } from "sonner";
import { Download, Landmark, BadgeCheck } from "lucide-react";

const inputCls = "mt-1 w-full bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: "", legal_name: "", pen_names: "", bio: "", tax_info: "", payout_details: "" });
  const [payments, setPayments] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loc, setLoc] = useState([]);
  const [membership, setMembership] = useState(null);
  const [params, setParams] = useSearchParams();

  const loadMembership = () => api.get("/membership/my").then((r) => setMembership(r.data));

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Membership renewed — thank you!");
          setParams({});
          loadMembership();
          api.get("/payments/my").then((r) => setPayments(r.data));
          return;
        }
        if (data.status === "expired") return;
        setTimeout(poll, 2000);
      } catch { setTimeout(poll, 2000); }
    };
    poll();
  }, [params, setParams]);

  const renew = async () => {
    try {
      const { data } = await api.post("/payments/membership/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const loadDocs = () => {
    api.get("/my/documents").then((r) => setDocs(r.data));
    api.get("/my/loc").then((r) => setLoc(r.data));
  };

  useEffect(() => {
    if (user) setForm({
      name: user.name || "", legal_name: user.legal_name || "",
      pen_names: (user.pen_names || []).join(", "), bio: user.bio || "",
      tax_info: user.tax_info || "", payout_details: user.payout_details || "" });
    api.get("/payments/my").then((r) => setPayments(r.data));
    loadDocs();
    loadMembership();
  }, [user]);

  const review = async (doc, action) => {
    let note = "";
    if (action === "request_changes") {
      note = window.prompt(`What changes are needed for "${doc.title}"?`) || "";
      if (!note.trim()) return;
    }
    try {
      await api.put(`/documents/${doc.id}/review`, { action, note });
      toast.success(action === "approve" ? "Document approved" : "Change request sent");
      loadDocs();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const save = async () => {
    try {
      const { data } = await api.put("/auth/profile", {
        ...form, pen_names: form.pen_names.split(",").map((s) => s.trim()).filter(Boolean) });
      setUser(data);
      toast.success("Profile saved");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="max-w-3xl" data-testid="profile-page">
      <h1 className="text-3xl font-bold tracking-tight text-neutral-900 font-heading mb-8">Author Profile</h1>
      <div className="bg-white border border-neutral-200 rounded-lg p-8 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-sm font-medium text-neutral-700">Display name</label>
            <input data-testid="profile-name-input" value={form.name} onChange={set("name")} className={inputCls} /></div>
          <div><label className="text-sm font-medium text-neutral-700">Legal name</label>
            <input data-testid="profile-legal-name-input" value={form.legal_name} onChange={set("legal_name")} className={inputCls} /></div>
        </div>
        <div><label className="text-sm font-medium text-neutral-700">Pen names (comma-separated)</label>
          <input data-testid="profile-pen-names-input" value={form.pen_names} onChange={set("pen_names")} className={inputCls} /></div>
        <div><label className="text-sm font-medium text-neutral-700">Bio</label>
          <textarea rows={3} value={form.bio} onChange={set("bio")} className={inputCls} /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-sm font-medium text-neutral-700">Tax info (e.g. W-9 status, TIN)</label>
            <input value={form.tax_info} onChange={set("tax_info")} className={inputCls} /></div>
          <div><label className="text-sm font-medium text-neutral-700">Payout details (e.g. bank / PayPal)</label>
            <input value={form.payout_details} onChange={set("payout_details")} className={inputCls} /></div>
        </div>
        <button data-testid="profile-save-button" onClick={save}
          className="px-5 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">Save profile</button>
      </div>

      <h2 className="text-xl font-semibold font-heading text-neutral-900 mt-10 mb-4">Membership</h2>
      <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-6" data-testid="membership-section">
        {!membership || membership.status === "none" ? (
          <p className="text-sm text-neutral-500">Your monthly membership starts with your first book submission payment — the first 30 days are included in your plan.</p>
        ) : (
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[240px]">
              <p className="font-medium text-neutral-900 flex items-center gap-2">
                <BadgeCheck size={17} className={membership.status === "expired" ? "text-red-500" : "text-emerald-600"} />
                {membership.plan_name} membership — ${membership.monthly_price?.toFixed(2)}/month
              </p>
              <p className="text-sm text-neutral-500 mt-1">
                {membership.status === "expired"
                  ? `Lapsed on ${new Date(membership.access_until).toLocaleDateString()} — renew to keep full access to your dashboard, sales and payouts.`
                  : `Access until ${new Date(membership.access_until).toLocaleDateString()} (${membership.days_left} days left).`}
              </p>
            </div>
            <span data-testid="membership-status" className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
              membership.status === "expired" ? "text-red-700 bg-red-50 border-red-200" :
              membership.status === "expiring" ? "text-amber-700 bg-amber-50 border-amber-200" :
              "text-emerald-700 bg-emerald-50 border-emerald-200"}`}>
              {membership.status}
            </span>
            <button data-testid="renew-membership-button" onClick={renew}
              className="px-5 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">
              Renew — ${membership.monthly_price?.toFixed(2)}
            </button>
            {(membership.modules || []).length > 0 && (
              <div className="w-full pt-4 border-t border-neutral-100" data-testid="membership-modules">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-neutral-400 mb-2">Included modules</p>
                <div className="flex flex-wrap gap-2">
                  {membership.modules.map((m) => (
                    <span key={m} className="px-2.5 py-1 rounded-full text-xs font-medium bg-neutral-100 text-neutral-700 border border-neutral-200">
                      {MODULE_LABELS[m] || m}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <h2 className="text-xl font-semibold font-heading text-neutral-900 mt-10 mb-4">Documents & Copyright</h2>
      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden mb-6" data-testid="my-documents-section">
        {docs.length === 0 ? <p className="p-6 text-sm text-neutral-400">No documents from your publisher yet.</p> : (
          <div className="divide-y divide-neutral-100">
            {docs.map((d) => (
              <div key={d.id} data-testid={`my-doc-${d.id}`} className="p-5 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[200px]">
                  <p className="font-medium text-neutral-900 text-sm">{d.title}</p>
                  <p className="text-xs text-neutral-400 mt-0.5">{CATEGORY_LABELS[d.category]} · {d.filename} · {new Date(d.created_at).toLocaleDateString()}</p>
                  {d.author_note && <p className="text-xs text-neutral-500 mt-1">Your note: {d.author_note}</p>}
                </div>
                <DocStatusBadge status={d.status} />
                <button data-testid={`my-doc-download-${d.id}`}
                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}/download`, "_blank")}
                  className="p-1.5 text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 rounded-md"><Download size={15} /></button>
                {d.status === "pending_review" && (
                  <div className="flex gap-2">
                    <button data-testid={`approve-doc-${d.id}`} onClick={() => review(d, "approve")}
                      className="px-3 py-1.5 rounded-md text-white bg-emerald-600 hover:bg-emerald-700 text-xs font-medium">Approve</button>
                    <button data-testid={`request-changes-doc-${d.id}`} onClick={() => review(d, "request_changes")}
                      className="px-3 py-1.5 border border-neutral-300 rounded-md text-xs font-medium text-neutral-700 hover:bg-neutral-50">Request changes</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {loc.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-6" data-testid="my-loc-section">
          <h3 className="font-semibold font-heading mb-4 flex items-center gap-2"><Landmark size={17} /> Library of Congress records</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {loc.map((r) => (
              <div key={r.id} className="border border-neutral-200 rounded-md p-4">
                <p className="font-medium text-neutral-900 text-sm">{r.book_title}</p>
                <p className="text-xs text-neutral-500 mt-1">LCCN: <span className="font-mono font-semibold">{r.lccn}</span>{r.year ? ` · ${r.year}` : ""}</p>
                {r.notes && <p className="text-xs text-neutral-400 mt-1">{r.notes}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      <h2 className="text-xl font-semibold font-heading text-neutral-900 mt-10 mb-4">Payments & Receipts</h2>
      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        {payments.length === 0 ? <p className="p-6 text-sm text-neutral-400">No payments yet.</p> : (
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-neutral-200 text-sm text-left">
            <thead><tr className="bg-neutral-50">
              {["Date", "Plan", "Amount", "Status"].map((h) => (
                <th key={h} className="px-6 py-3 font-medium text-neutral-500 uppercase tracking-wider text-xs">{h}</th>))}
            </tr></thead>
            <tbody className="divide-y divide-neutral-100">
              {payments.map((p) => (
                <tr key={p.id} data-testid={`payment-row-${p.id}`}>
                  <td className="px-6 py-3">{new Date(p.created_at).toLocaleDateString()}</td>
                  <td className="px-6 py-3 font-medium">{p.plan_name}</td>
                  <td className="px-6 py-3">${p.amount.toFixed(2)}</td>
                  <td className="px-6 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${p.payment_status === "paid" ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-amber-700 bg-amber-50 border-amber-200"}`}>
                      {p.payment_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}
